import { APP_BASE_HREF } from '@angular/common';
import {HTTP_INTERCEPTORS, HttpClientModule} from '@angular/common/http';
import { NgModule, APP_INITIALIZER } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { UserProfileModule } from 'cad/user-profile/user-profile.module';
import { AuthInterceptor } from 'common/services/http-interceptor/auth-interceptor';
import { DateInterceptor } from 'common/services/http-interceptor/date-interceptor';
import { EmptyStringInterceptor } from 'common/services/http-interceptor/empty-string-interceptor';
import { HeaderInterceptor } from 'common/services/http-interceptor/header-interceptor';
import { IdleInterceptor } from 'common/services/http-interceptor/idle-interceptor';
import { PendingInterceptor } from 'common/services/http-interceptor/pending-interceptor';
import { UserInterceptor } from 'common/services/http-interceptor/user-interceptor';
import { RunAsUserModule } from 'src/cad/login/run-as-user.module';
import { CadAppComponentsModule } from 'src/features/common/cad-app/cad-app-components.module';
import { CadAppController } from 'src/features/common/cad-app/cad-app.controller';
import { AppComponent } from 'src/features/common/cad-app/view/app/app.component';
import { SearchDropdownComponent } from 'src/features/common/cad-app/view/search-dropdown/search-dropdown.component';
import { SecurityService } from 'src/features/common/security/security.service';
import { ControllerFactoryComponent } from 'src/framing/controller-factory.component';
import { FramingRootComponent } from 'src/framing/framing-root.component';
import { CoreModule } from './core/core.module';
import { DashboardModule } from './dashboard/dashboard.module';
import { RedirectGuard } from './informational-postings/redirect-guard';
import { LoginModule } from './login/login.module';
import { SharedModule } from './shared/shared.module';
import { ForbiddenComponent } from './status/forbidden/forbidden.component';
import { InternalServerErrorComponent } from './status/internal-server-error/internal-server-error.component';
import { NotFoundComponent } from './status/not-found/not-found.component';
import { StatusModule } from './status/status.module';
import { ServiceWorkerModule, SwRegistrationOptions } from '@angular/service-worker';
import { environment } from 'src/environments/environment';
import { MSALInstanceFactory, MSALInterceptorConfigFactory, initializeMsal } from './auth-config';
import {
  MsalModule,
  MsalService,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
} from "@azure/msal-angular";
import {CustomMsalInterceptor} from "common/services/http-interceptor/custom-msal.interceptor";
import {CaptureAuthTokenInterceptor} from "common/services/http-interceptor/capture-auth-token-interceptor";
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';


@NgModule({
  declarations: [ FramingRootComponent, AppComponent ],
  bootstrap: [ FramingRootComponent ],
  imports: [
    BrowserAnimationsModule,
    CoreModule,
    SharedModule,
    StatusModule,
    CadAppComponentsModule,
    BrowserModule.withServerTransition({
      appId: 'app',
    }),
    FormsModule,
    HttpClientModule,
    MsalModule,
    RouterModule.forRoot(
      [
        {
          path: '',
          component: ControllerFactoryComponent,
          data: {
            featureCadApp: {
              name: 'CadApp',
              controller: CadAppController,
              model: {
                isSearching: false,
                footerLogo: 'resources/img/logo.svg',
                disableSearchButton: false,
                disableActionMenu: false
              },
              view: {
                app: AppComponent,
                search: null,
                searchDropdown: SearchDropdownComponent
              },
            }
          },
          children: [
            {
              path: '',
              pathMatch: 'full',
              loadChildren: () => DashboardModule,
              canActivate: [ SecurityService ],
              data: { SecurityModel: { authorization: 'anonymous' } }
            },
            { path: 'login', loadChildren: () => LoginModule },
            { path: 'run-as-user', loadChildren: () => RunAsUserModule },
            {
              path: 'administration',
              loadChildren: () => import('./administration/administration.module')
                .then((m) => { return m.AdministrationModule; })
                .catch((err) => {
                  console.log('Lazy Loading Error in AdministrationModule: ');
                  console.log(err);
                  return [];
                }),
              canActivate: [ SecurityService ],
              data: { SecurityModel: { authorization: 'CustomerActivities.menu.administration' } }
            },
            {
              path: 'contracts',
              loadChildren: () => import('./contracts/contracts.module')
                .then((m) => { return m.ContractsModule; })
                .catch((err) => {
                  console.log('Lazy Loading Error in ContractsModule: ');
                  console.log(err);
                  return [];
                }),
              canActivate: [ SecurityService ],
              data: { SecurityModel: { authorization: 'CustomerActivities.menu.contracts' } },
            },
            {
              path: 'data-administration',
              loadChildren: () => import('cad/data-administration/data-administration.module')
                .then((m) => { return m.DataAdministrationModule; })
                .catch((err) => {
                  console.log('Lazy Loading Error in DataAdministrationModule: ');
                  console.log(err);
                  return [];
                }),
              canActivate: [ SecurityService ],
              data: { SecurityModel: { authorization: 'CustomerActivities.menu.dataAdministration' } },
            },
            {
              path: 'structured-products',
              loadChildren: () => import('./structured-products/structured-products.module')
                .then((m) => { return m.StructuredProductsModule; })
                .catch((err) => {
                  console.log('Lazy Loading Error in StructuredProductsModule: ');
                  console.log(err);
                  return [];
                }),
              canActivate: [ SecurityService ],
              data: { SecurityModel: { authorization: 'CustomerActivities.menu.structuredProducts' } },
            },
            {
              path: 'physical',
              loadChildren: () => import('./physical/physical.module')
                .then((m) => { return m.PhysicalModule; })
                .catch((err) => {
                  console.log('Lazy Loading Error in PhysicalModule: ');
                  console.log(err);
                  return [];
                }),
              canActivate: [ SecurityService ],
              data: { SecurityModel: { authorization: 'CustomerActivities.menu.physical' } },
            },
            {
              path: 'capacity-analysis',
              loadChildren: () => import('./capacity-analysis/capacity-analysis.module')
                .then((m) => { return m.CapacityAnalysisModule; })
                .catch((err) => {
                  console.log('Lazy Loading Error in CapacityAnalysisModule: ');
                  console.log(err);
                  return[];
                }),
              canActivate: [ SecurityService ],
              data: { SecurityModel: { authorization: 'CustomerActivities.menu.capacityAnalysis' } },
            },
            {
              path: 'portfolio-management',
              loadChildren: () => import('./portfolio-management/portfolio-management.module')
                .then((m) => { return m.PortfolioManagementModule; })
                .catch((err) => {
                  console.log('Lazy Loading Error in PortfolioManagementModule: ');
                  console.log(err);
                  return [];
                }),
              canActivate: [ SecurityService ],
              data: { SecurityModel: { authorization: 'CustomerActivities.menu.portfolioManagement' } },
            },
            {
              path: 'reports',
              loadChildren: () => import('./reports/reports.module')
                .then((m) => { return m.ReportsModule; })
                .catch((err) => {
                  console.log('Lazy Loading Error in ReportsModule: ');
                  console.log(err);
                  return [];
                })
            },
            {
              path: 'workflow',
              loadChildren: () => import('./workflow/workflow.module')
                .then((m) => { return m.WorkflowModule; })
                .catch((err) => {
                  console.log('Lazy Loading Error in WorkflowModule: ');
                  console.log(err);
                  return [];
                }),
              canActivate: [ SecurityService ],
              data: {
                SecurityModel: {
                  authorization: 'CustomerActivities.menu.workflow.workflowRunner',
                  actionContext: 'workflowRunner'
                }
              },
            },
            {
              path: 'user-profile',
              loadChildren: () => UserProfileModule,
              canActivate: [ SecurityService ],
              data: {
                SecurityModel: {
                  authorization: 'CustomerActivities.menu.maintainProfile',
                  actionContext: 'maintainProfile'
                }
              }
            },
            {
              path: 'invoicing',
              loadChildren: () => import('./invoicing/invoicing.module')
                .then((m) => { return m.InvoicingModule; })
                .catch((err) => {
                  console.log('Lazy Loading Error in InvoicingModule: ');
                  console.log(err);
                  return [];
                }),
              canActivate: [ SecurityService ],
              data: { SecurityModel: { authorization: 'CustomerActivities.menu.invoicing' } },
            },
            {
              path: 'nominations',
              loadChildren: () => import('./nominations/nominations.module')
                .then((m) => { return m.NominationsModule; })
                .catch((err) => {
                  console.log('Lazy Loading Error in NominationsModule: ');
                  console.log(err);
                  return [];
                }),
              canActivate: [ SecurityService ],
              data: { SecurityModel: { authorization: 'CustomerActivities.menu.nominations' } },
            },
            {
              path: 'flowing-gas',
              loadChildren: () => import('./flowing-gas/flowing-gas.module')
                .then((m) => { return m.FlowingGasModule; })
                .catch((err) => {
                  console.log('Lazy Loading Error in FlowingGasModule: ');
                  console.log(err);
                  return [];
                }),
              canActivate: [ SecurityService ],
              data: { SecurityModel: { authorization: 'CustomerActivities.menu.flowManagement' } },
            },
            {
              path: 'infopost',
              loadChildren: () => import('./infopost/infopost.module')
                .then((m) => { return m.InfoPostModule; })
                .catch((err) => {
                  console.log('Lazy Loading Error in InfoPostModule: ');
                  console.log(err);
                  return [];
                }),
              canActivate: [ SecurityService ],
              data: { SecurityModel: { authorization: 'CustomerActivities.menu.infopost' } },
            },
            {
              path: 'capacity-release',
              loadChildren: () => import('./capacity-release/capacity-release.module')
                .then((m) => { return m.CapacityReleaseModule; })
                .catch((err) => {
                  console.log('Lazy Loading Error in CapacityReleaseModule: ');
                  console.log(err);
                  return [];
                }),
              canActivate: [ SecurityService ],
              data: { SecurityModel: { authorization: 'CustomerActivities.menu.capacityRelease' } },
            },
            { path: 'infoPost', canActivate: [ RedirectGuard ], component: NotFoundComponent },
            {
              path: 'site-map',
              loadChildren: () => import('./site-map/site-map.module')
                .then((m) => { return m.SiteMapModule; })
                .catch((err) => {
                  console.log('Lazy Loading Error in SiteMapModule: ');
                  console.log(err);
                  return [];
                }),
            },
            { path: 'dashboard', pathMatch: 'full', redirectTo: '' },
            { path: '404', component: NotFoundComponent },
            { path: '403', component: ForbiddenComponent },
            { path: 'forbidden', component: ForbiddenComponent },
            { path: '500', component: InternalServerErrorComponent },
            { path: '**', component: NotFoundComponent }
          ]
        }
      ],
      {
        enableTracing: false,
        useHash: true,
      }),
    ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.isReleaseBuild }),
  ],
  providers: [
    {
      provide: SwRegistrationOptions,
      useFactory: () => ({ enabled: environment.isReleaseBuild }),
    },
    { provide: APP_BASE_HREF, useValue: '/' },
    { provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory  },
    { provide: MSAL_INTERCEPTOR_CONFIG, useFactory: MSALInterceptorConfigFactory },
    { provide: APP_INITIALIZER, useFactory: initializeMsal, deps: [MSAL_INSTANCE], multi: true },
    MsalService,
    MsalBroadcastService,
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { subscriptSizing: 'dynamic' } },
    { provide: PendingInterceptor, useClass: PendingInterceptor, deps: [] },
    // order matters, please do not change the order of interceptor without reason.
    { provide: HTTP_INTERCEPTORS, useExisting: PendingInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: HeaderInterceptor, multi: true, },
    { provide: HTTP_INTERCEPTORS, useClass: UserInterceptor, multi: true, },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true, },
    { provide: HTTP_INTERCEPTORS, useClass: CustomMsalInterceptor, multi: true  },
    { provide: HTTP_INTERCEPTORS, useClass: CaptureAuthTokenInterceptor, multi: true  },
    // { provide: HTTP_INTERCEPTORS, useClass: TimingInterceptor, multi: true, },
    { provide: HTTP_INTERCEPTORS, useClass: DateInterceptor, multi: true, },
    { provide: HTTP_INTERCEPTORS, useClass: EmptyStringInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: IdleInterceptor, multi: true, },
    RedirectGuard
  ],
})
export class AppModule { }
